import { Component, inject, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { LiveUpdateService } from "../../../../../../../../shared/src/lib/services/live-update.service";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RunnerTdComponent } from "./runner-td/runner-td.component";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { RouterLink } from "@angular/router";
import { getStatus, sortHighlightRunners } from "../../../../../../../../shared/src/lib/utils/market";
import { OverlayComponent } from "../../../../../components/overlay/overlay.component";
import { MomentDatePipe } from "../../../../../../../../shared/src/lib/pipe/moment-date.pipe";
import { TranslateModule } from "@ngx-translate/core";
import { MarketRulesComponent } from "../../../components/market-rules/market-rules.component";
import { BetPlacementService } from "../../../../../../../../shared/src/lib/services/bet-placement.service";
import { FeatureFlagDirective } from "../../../../../../../../shared/src/lib/directives/feature-flag.directive";
import { MarketOpsService } from "../../../market-layout/market/market-ops.service";
import { ExRateService } from "../../../../../../../../shared/src/lib/services/ex-rate.service";
import { LoaderService } from "../../../../../../../../shared/src/lib/services/loader.service";
import { MyMarketsService } from "../../../my-markets/my-markets.service";
import { WhiteLabelConfigService } from "../../../../../../../../shared/src/lib/services/white-label-config.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "[app-h-row]",
  imports: [
    MatIconModule,
    MatTooltipModule,
    RunnerTdComponent,
    RouterLink,
    NgClass,
    OverlayComponent,
    DecimalPipe,
    MomentDatePipe,
    TranslateModule,
    FeatureFlagDirective,
    AsyncPipe,
  ],
  templateUrl: "./h-row.component.html",
  styleUrl: "./h-row.component.scss",
})
export class HRowComponent implements OnInit, OnDestroy {
  @Input() market: any;
  readonly destroy$ = new Subject<void>();
  liveUpdateService = inject(LiveUpdateService);
  exRateService = inject(ExRateService);
  placementService = inject(BetPlacementService);
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  updatedRunners = signal({});
  marketRunners: any[] = [];
  isInPlay = signal<boolean>(false);
  marketStatus: string = "";
  loader = inject(LoaderService);
  openBetsLoading$!: Observable<boolean>;
  myMarketsService = inject(MyMarketsService);
  allowPlacement = true;
  timeDiffCheckInterval: any = null;

  constructor(private bottomSheet: MatBottomSheet) {}

  ngOnInit() {
    this.openBetsLoading$ = this.loader.isLoading("/bf/current-user-order");
    if (this.market) {
      this.getMarketUpdates(this.market.marketId);
      this.checkPreMatchPlacement();
    }
    this.marketRunners = sortHighlightRunners(this.market?.runners);
    this.initValues();
  }

  initValues() {
    // console.log("init is called", this.market?.event?.name, initialValues);
    const initialValues = this.market?.mc;
    if (initialValues) this.handleMarketUpdate(initialValues);
  }

  checkPreMatchPlacement() {
    const openDate = this.market?.event?.openDate;
    const placementPermitted = this.wlConfigService.showFeature("preMatchPlacement");
    this.allowPlacement = !placementPermitted ? this.wlConfigService.isWithin45Minutes(openDate) : placementPermitted;
    if (!placementPermitted) {
      this.timeDiffCheckInterval = setInterval(() => {
        // console.log(this.wlConfigService.isWithin45Minutes(openDate));
        this.allowPlacement = this.wlConfigService.isWithin45Minutes(openDate);
      }, 1000);
    }
  }

  getMarketUpdates(marketId: string) {
    this.liveUpdateService
      .getData("market", { field: "id", value: marketId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) this.handleMarketUpdate(data);
        },
      });
  }

  handleMarketUpdate(update: any) {
    // console.log(update);
    if (update?.r && update?.r?.length > 0) this.handleRunnerUpdates(update?.r);
    this.marketStatus = update?.st !== undefined ? getStatus(update?.st) : "";
    this.isInPlay.set(update?.ipl);
    if (update?.tv) this.market["totalMatched"] = update?.tv;
  }

  handleRunnerUpdates(runners: any) {
    this.updatedRunners.set(this.marketOps.transformRunners(runners));
  }

  handleRules(data: any) {
    this.bottomSheet.open(MarketRulesComponent, {
      data,
      panelClass: "rules-sheet",
    });
  }

  getRunnerUpdate(selectionId: number): Observable<any> {
    const runners: any = this.updatedRunners();
    return runners[selectionId];
  }

  addToMyMarkets() {
    this.myMarketsService.storeMarket(this.market?.marketId);
  }

  removeFromMyMarkets() {
    this.myMarketsService.removeMarket(this.market?.marketId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timeDiffCheckInterval) clearInterval(this.timeDiffCheckInterval);
  }
}
